<template>
    <div class="flex flex-wrap justify-content-end align-items-center gap-2 mb-2 pt-2">
        <DatePicker
            v-model="periodo"
            selectionMode="range"
            dateFormat="dd/mm/yy"
            placeholder="Período"
            showButtonBar
            :manualInput="false"
            @update:modelValue="onPeriodoChange"
        />
        <div class="flex align-items-center gap-1">
            <Button
                type="button"
                icon="pi pi-list"
                :label="cliente?.nome ? cliente.nome.trim() : 'Selecionar cliente'"
                severity="secondary"
                outlined
                @click="abrirModalClientes"
            />
            <Button
                v-if="cliente"
                type="button"
                icon="pi pi-times"
                severity="secondary"
                text
                rounded
                aria-label="Limpar cliente"
                @click="cliente = null"
            />
        </div>
    </div>

    <Dialog
        v-model:visible="modalClientesVisivel"
        header="Selecionar cliente"
        :style="{ width: '40rem', maxWidth: '95vw' }"
        modal
        dismissableMask
    >
        <div class="flex flex-column gap-3">
            <InputText
                v-model="clienteModalFiltro"
                placeholder="Buscar cliente por nome..."
                class="w-full"
                @update:modelValue="onFiltroModalClientesInput"
            />

            <DataTable
                :value="clientesModalLista"
                :loading="clientesModalLoading"
                lazy
                paginator
                :rows="clientesModalPaginacao.rows"
                :totalRecords="clientesModalPaginacao.totalRecords"
                :first="clientesModalPaginacao.first"
                selectionMode="single"
                dataKey="id"
                size="small"
                stripedRows
                rowHover
                @page="onClientesModalPage"
                @row-click="onSelecionarClienteModal"
            >
                <template #empty>
                    <div class="text-center p-3">Nenhum cliente encontrado</div>
                </template>
                <Column field="nome" header="Nome" />
                <Column v-if="temColunaDocumento" field="documento" header="Documento" style="width: 12rem">
                    <template #body="{ data }">{{ formatDocumento(data.documento) }}</template>
                </Column>
            </DataTable>
        </div>

        <template #footer>
            <div class="flex justify-content-between align-items-center w-full">
                <Button
                    type="button"
                    icon="pi pi-filter-slash"
                    label="Limpar cliente"
                    severity="secondary"
                    outlined
                    :disabled="!cliente"
                    @click="limparClienteModal"
                />
                <Button
                    type="button"
                    label="Fechar"
                    severity="secondary"
                    text
                    @click="modalClientesVisivel = false"
                />
            </div>
        </template>
    </Dialog>

    <div class="flex flex-column md:flex-row gap-2 pt-2">
        <Card class="flex-1" style="text-align: center">
            <template #content>
                <div class="font-bold text-2xl">R$ {{ formatMoney(financeiroInfos.total) }}</div>
                <div class="font-medium text-blue-500"><b>Total de Contas</b></div>
                <small class="text-color-secondary">por vencimento (pago: por pagamento)</small>
            </template>
        </Card>

        <Card class="flex-1" style="text-align: center">
            <template #content>
                <div class="font-bold text-2xl">R$ {{ formatMoney(financeiroInfos.pago) }}</div>
                <div class="font-medium text-green-500"><b>Total Pago</b></div>
                <small class="text-color-secondary">por vencimento (pago: por pagamento)</small>
            </template>
        </Card>

        <Card class="flex-1" style="text-align: center">
            <template #content>
                <div class="font-bold text-2xl">R$ {{ formatMoney(financeiroInfos.pendente) }}</div>
                <div class="font-medium text-red-500"><b>Total Pendente</b></div>
                <small class="text-color-secondary">por vencimento (pago: por pagamento)</small>
            </template>
        </Card>
    </div>

    <div class="flex flex-column xl:flex-row gap-2 my-2">
        <Card class="flex-1 chart-card" style="min-height: 400px;">
            <template #title>
                <div>
                    <h4 class="m-0">PAGO vs PENDENTE</h4>
                    <small class="text-color-secondary font-normal">por vencimento (pago: por pagamento)</small>
                </div>
            </template>
            <template #content>
                <div class="chart-area">
                    <div v-if="_isMounted && hasDonutData" style="max-width: 300px; margin: auto;">
                        <Chart :key="`paid-${query}`" type="doughnut" :data="paidPendingChartData" :options="chartOptions" />
                    </div>
                    <div v-else-if="!isLoading.donut" class="text-center p-4">Sem dados no período.</div>
                    <div v-if="isLoading.donut" class="chart-loader">
                        <ProgressSpinner style="width: 40px; height: 40px" strokeWidth="6" />
                        <span>CARREGANDO GRÁFICO...</span>
                    </div>
                </div>
            </template>
        </Card>

        <Card class="flex-1 chart-card" style="min-height: 400px;">
            <template #title>
                <div>
                    <h4 class="m-0">COMPARATIVO MENSAL</h4>
                    <small class="text-color-secondary font-normal">por vencimento (pago: por pagamento)</small>
                </div>
            </template>
            <template #content>
                <div class="chart-area">
                    <Chart
                        v-if="_isMounted && monthlyFinancialChartData?.labels?.length && monthlyFinancialChartData?.datasets?.length"
                        :key="`monthly-${query}`"
                        type="bar"
                        :data="monthlyFinancialChartData"
                        :options="chartOptionsMultiAxis"
                    />
                    <div v-else-if="!isLoading.bar" class="text-center p-4">Sem dados no período.</div>
                    <div v-if="isLoading.bar" class="chart-loader">
                        <ProgressSpinner style="width: 40px; height: 40px" strokeWidth="6" />
                        <span>CARREGANDO GRÁFICO...</span>
                    </div>
                </div>
            </template>
        </Card>
    </div>

    <CadastroBoletoCharts :query="query" />
</template>

<script>
import DatePicker from 'primevue/datepicker';
import Button from 'primevue/button';
import Dialog from 'primevue/dialog';
import InputText from 'primevue/inputtext';
import DataTable from 'primevue/datatable';
import Column from 'primevue/column';
import SafeChart from '@/components/SafeChart.vue';
import Card from 'primevue/card';
import ProgressSpinner from 'primevue/progressspinner';
import Conta from '@/service/conta.js';
import Cliente from '@/service/cliente.js';
import CadastroBoletoCharts from './components/CadastroBoletoCharts.vue';
import Mask from '@/utils/Mask.js';

const ymd = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

const periodoPadrao = () => {
    const hoje = new Date();
    return [new Date(hoje.getFullYear(), hoje.getMonth(), 1), hoje];
};

export default {
    components: {
        DatePicker,
        Button,
        Dialog,
        InputText,
        DataTable,
        Column,
        Chart: SafeChart,
        Card,
        ProgressSpinner,
        CadastroBoletoCharts,
    },
    data() {
        const padrao = periodoPadrao();
        return {
            isLoading: { donut: false, bar: false },
            reqId: { donut: 0, bar: 0, clientesModal: 0 },
            periodo: padrao,
            periodoAtivo: padrao,
            cliente: null,
            modalClientesVisivel: false,
            clienteModalFiltro: '',
            clienteModalDebounceTimer: null,
            clientesModalLista: [],
            clientesModalLoading: false,
            clientesModalPaginacao: {
                page: 1,
                rows: 15,
                totalRecords: 0,
                first: 0,
            },
            financeiroInfos: {
                total: 0,
                pago: 0,
                pendente: 0,
            },
            paidPendingChartData: {},
            monthlyFinancialChartData: {},
            chartOptions: {
                responsive: true,
                plugins: {
                    legend: {
                        position: 'top',
                    },
                    tooltip: {
                        callbacks: {
                            label: function (context) {
                                let label = context.label || '';
                                if (label) {
                                    label += ': ';
                                }
                                if (context.parsed !== null) {
                                    label += 'R$ ' + context.parsed.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
                                }
                                return label;
                            },
                        },
                    },
                },
            },
            chartOptionsMultiAxis: {
                responsive: true,
                plugins: {
                    legend: {
                        position: 'top',
                    },
                    tooltip: {
                        callbacks: {
                            label: function (context) {
                                let label = context.dataset.label || '';
                                if (label) {
                                    label += ': ';
                                }
                                if (context.parsed.y !== null) {
                                    label += 'R$ ' + context.parsed.y.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
                                }
                                return label;
                            },
                        },
                    },
                },
                scales: {
                    x: {
                        stacked: false,
                    },
                    y: {
                        type: 'linear',
                        display: true,
                        position: 'left',
                        beginAtZero: true,
                        title: {
                            display: true,
                            text: 'Valor (R$)',
                        },
                    },
                },
            },

            _isMounted: false,
        };
    },
    computed: {
        clienteId() {
            return this.cliente?.id ?? null;
        },
        query() {
            const [inicio, fim] = this.periodoAtivo || [];
            if (!inicio || !fim) return '';
            const params = [`data_inicio=${ymd(inicio)}`, `data_fim=${ymd(fim)}`];
            if (this.clienteId) params.push(`cliente_id=${this.clienteId}`);
            return params.join('&');
        },
        hasDonutData() {
            const ds = this.paidPendingChartData?.datasets?.[0]?.data;
            return Array.isArray(ds) && ds.some((v) => Number(v) > 0);
        },
        temColunaDocumento() {
            return this.clientesModalLista.some((item) => item && 'documento' in item);
        },
    },
    watch: {
        query(novaQuery) {
            if (novaQuery && this._isMounted) {
                this.loadAll();
            }
        },
    },
    mounted() {
        this._isMounted = true;
        this.loadAll();
    },
    beforeUnmount() {
        this._isMounted = false;
        if (this.clienteModalDebounceTimer) {
            clearTimeout(this.clienteModalDebounceTimer);
        }
    },
    methods: {
        onPeriodoChange(valor) {
            if (!valor) {
                const padrao = periodoPadrao();
                this.periodo = padrao;
                this.periodoAtivo = padrao;
                return;
            }
            if (valor[0] && valor[1]) {
                this.periodoAtivo = [valor[0], valor[1]];
            }
        },
        abrirModalClientes() {
            this.modalClientesVisivel = true;
            this.clienteModalFiltro = '';
            this.clientesModalPaginacao.page = 1;
            this.clientesModalPaginacao.first = 0;
            this.carregarClientesModal(1);
        },
        onFiltroModalClientesInput() {
            if (this.clienteModalDebounceTimer) {
                clearTimeout(this.clienteModalDebounceTimer);
            }
            this.clienteModalDebounceTimer = setTimeout(() => {
                this.clientesModalPaginacao.page = 1;
                this.clientesModalPaginacao.first = 0;
                this.carregarClientesModal(1);
            }, 300);
        },
        onClientesModalPage(event) {
            const novaPagina = (event.page ?? 0) + 1;
            this.clientesModalPaginacao.page = novaPagina;
            this.clientesModalPaginacao.first = event.first ?? 0;
            this.carregarClientesModal(novaPagina);
        },
        async carregarClientesModal(page = 1) {
            const reqId = ++this.reqId.clientesModal;
            this.clientesModalLoading = true;
            try {
                const texto = encodeURIComponent((this.clienteModalFiltro || '').trim());
                const response = await Cliente.list(`filter[nome]=${texto}&order[nome]=asc&page=${page}`);
                if (!this._isMounted || reqId !== this.reqId.clientesModal) return;
                const payload = response.data || {};
                this.clientesModalLista = payload.data || [];
                const perPage = Number(payload.per_page) || 15;
                const currentPage = Number(payload.current_page) || page;
                this.clientesModalPaginacao.rows = perPage;
                this.clientesModalPaginacao.page = currentPage;
                this.clientesModalPaginacao.totalRecords = Number(payload.total) || 0;
                this.clientesModalPaginacao.first = (currentPage - 1) * perPage;
            } catch (error) {
                if (!this._isMounted || reqId !== this.reqId.clientesModal) return;
                console.error('Erro ao carregar lista de clientes:', error);
                this.clientesModalLista = [];
                this.clientesModalPaginacao.totalRecords = 0;
                this.$toast.add({
                    severity: 'error',
                    summary: 'Erro',
                    detail: error.response?.data?.msg || 'Falha ao carregar clientes',
                    life: 5000,
                });
            } finally {
                if (reqId === this.reqId.clientesModal) {
                    this.clientesModalLoading = false;
                }
            }
        },
        onSelecionarClienteModal(event) {
            if (!event?.data) return;
            this.cliente = event.data;
            this.modalClientesVisivel = false;
        },
        limparClienteModal() {
            this.cliente = null;
            this.modalClientesVisivel = false;
        },
        formatDocumento(doc) {
            if (!doc) return '—';
            const limpo = String(doc).replace(/\D/g, '');
            if (limpo.length === 14) return Mask.cnpj(limpo);
            if (limpo.length === 11) return Mask.cpf(limpo);
            return doc;
        },
        loadAll() {
            this.loadFinanceiroData();
            this.loadMonthlyFinancialChart();
        },

        async loadFinanceiroData() {
            if (!this.query) return;
            const reqId = ++this.reqId.donut;
            this.isLoading.donut = true;
            try {
                const response = await Conta.valorTotal(this.query);
                if (!this._isMounted || reqId !== this.reqId.donut) return;
                const data = response.data || {};

                this.financeiroInfos.total = data.total ?? 0;
                this.financeiroInfos.pago = data.pago ?? 0;
                this.financeiroInfos.pendente = data.pendente ?? 0;

                this.loadPaidPendingChart(data);
            } catch (error) {
                if (!this._isMounted || reqId !== this.reqId.donut) return;
                console.error('Erro ao carregar totais financeiros:', error);
                this.$toast.add({
                    severity: 'error',
                    summary: 'Erro',
                    detail: error.response?.data?.msg || 'Falha ao carregar Totais',
                    life: 5000,
                });
            } finally {
                if (reqId === this.reqId.donut) this.isLoading.donut = false;
            }
        },

        loadPaidPendingChart(data) {
            if (!this._isMounted) return;
            this.paidPendingChartData = {
                labels: ['Total Pago', 'Total Pendente'],
                datasets: [
                    {
                        data: [Number(data.pago) || 0, Number(data.pendente) || 0],
                        backgroundColor: ['#22C55E', '#EF4444'],
                        hoverBackgroundColor: ['#16A34A', '#DC2626'],
                    },
                ],
            };
        },

        async loadMonthlyFinancialChart() {
            if (!this.query) return;
            const reqId = ++this.reqId.bar;
            this.isLoading.bar = true;
            try {
                const response = await Conta.valorMensal(this.query);
                if (!this._isMounted || reqId !== this.reqId.bar) return;
                const data = response.data || {};
                const labels = data.labels || [];

                if (!labels.length) {
                    this.monthlyFinancialChartData = { labels: [], datasets: [] };
                    return;
                }

                this.monthlyFinancialChartData = {
                    labels,
                    datasets: [
                        {
                            type: 'bar',
                            label: 'Valor Total Pago',
                            backgroundColor: '#3B82F6',
                            data: data.pago || [],
                            yAxisID: 'y',
                        },
                        {
                            type: 'bar',
                            label: 'Valor Total de Contas',
                            backgroundColor: '#EC4899',
                            data: data.total || [],
                            yAxisID: 'y',
                        },
                    ],
                };
            } catch (error) {
                if (!this._isMounted || reqId !== this.reqId.bar) return;
                console.error('Erro ao carregar dados do gráfico financeiro mensal:', error);
                this.$toast.add({
                    severity: 'error',
                    summary: 'Erro',
                    detail: error.response?.data?.msg || 'Falha ao carregar Gráfico Mensal',
                    life: 5000,
                });
            } finally {
                if (reqId === this.reqId.bar) this.isLoading.bar = false;
            }
        },

        formatMoney(valor) {
            if (!valor) return Mask.money(0);
            return Mask.money(valor);
        },
    },
};
</script>

<style scoped>
.chart-card {
    min-width: 0;
}
.chart-area {
    position: relative;
    min-height: 280px;
}
.chart-loader {
    position: absolute;
    inset: 0;
    background: rgba(26, 26, 26, 0.7);
    backdrop-filter: blur(2px);
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    z-index: 10;
}
:global(html:not(.my-app-dark) .chart-loader) {
    background: rgba(255, 255, 255, 0.7);
}
.chart-loader span {
    margin-top: 12px;
    font-size: 0.85rem;
    font-weight: 700;
    color: #f97316;
    letter-spacing: 0.05em;
}
:deep(.p-datatable-tbody > tr) {
    cursor: pointer;
}
</style>
